/**
 * @file shop-create-form.ts
 * @description Форма «Новый магазин» без разметки (phase19.28 этап А;
 * uiux-platform-shops §3; PL2, PL4, PL5): значение формы, адрес из
 * названия транслитом, проверка полей и сборка тела `POST /admin/platform/shops`.
 *
 * Slug проверяется общим `validateShopSlug` (тот же, что у API), занятость —
 * `GET slug-check` при вводе и окончательно 409 при создании. Владелец —
 * одно поле почты: `GET owner-lookup` нашёл пользователя — назначаем его
 * (`existing`), не нашёл — приглашаем нового (`new`); `role_conflict` — у
 * человека роль платформы, владельцем он быть не может (PL2).
 */

import { SHOP_SLUG_ERROR_MESSAGES, SHOP_SLUG_MAX_LENGTH, validateShopSlug } from "@chaiberi/shared";

import type { CreateShopPayload, OwnerLookup } from "../api-platform-shops";
import type { ShopCreateField } from "./shop-errors";

export interface ShopCreateValue {
  displayName: string;
  slug: string;
  /** Slug правили руками — транслит из названия больше не подставляем. */
  slugTouched: boolean;
  timeZone: string;
  ownerEmail: string;
  ownerName: string;
  contactEmail: string;
}

export const EMPTY_SHOP_CREATE: ShopCreateValue = {
  displayName: "",
  slug: "",
  slugTouched: false,
  timeZone: "",
  ownerEmail: "",
  ownerName: "",
  contactEmail: "",
};

const TRANSLIT: Readonly<Record<string, string>> = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z", и: "i", й: "y",
  к: "k", л: "l", м: "m", н: "n", о: "o", п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f",
  х: "h", ц: "ts", ч: "ch", ш: "sh", щ: "sch", ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
};

/** «Лавка трав» → «lavka-trav»: латиница, цифры и дефис, без дефисов по краям. */
export function slugFromName(name: string): string {
  const latin = [...name.toLowerCase()].map((ch) => TRANSLIT[ch] ?? ch).join("");
  return latin
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SHOP_SLUG_MAX_LENGTH)
    .replace(/-+$/g, "");
}

/** Изменение названия: пока slug не трогали, он следует за названием. */
export function withDisplayName(value: ShopCreateValue, displayName: string): ShopCreateValue {
  return value.slugTouched ? { ...value, displayName } : { ...value, displayName, slug: slugFromName(displayName) };
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isEmail(value: string): boolean {
  return EMAIL.test(value.trim());
}

/** Ответ owner-lookup относится к этой почте (ответ на прежний ввод — не считается). */
export function lookupFor(lookup: OwnerLookup | null, email: string): OwnerLookup | null {
  return lookup && lookup.email.trim().toLowerCase() === email.trim().toLowerCase() ? lookup : null;
}

export const ROLE_CONFLICT_TEXT = "У этого пользователя роль платформы — владельцем магазина он быть не может";
export const OWNER_INACTIVE_TEXT = "Пользователь отключён — включите его в «Пользователях» или укажите другого";

/** Почему найденный пользователь не может стать владельцем (реестр кодов owner-lookup). */
export const OWNER_CONFLICT_TEXT: Readonly<Record<NonNullable<OwnerLookup["conflict"]>, string>> = {
  role_conflict: ROLE_CONFLICT_TEXT,
  owner_inactive: OWNER_INACTIVE_TEXT,
};

export type ShopCreateErrors = Partial<Record<ShopCreateField, string>>;

/** Подсказка/ошибка slug при вводе; null — пусто, ещё не о чем говорить. */
export function slugIssue(slug: string): string | null {
  if (!slug) return null;
  const result = validateShopSlug(slug);
  return result.error ? SHOP_SLUG_ERROR_MESSAGES[result.error] : null;
}

/** Проверка перед отправкой: обязательны название, slug, пояс, почта владельца (PL4). */
export function validateShopCreate(value: ShopCreateValue, lookup: OwnerLookup | null): ShopCreateErrors {
  const owner = lookup?.user ?? null;
  const errors: ShopCreateErrors = {};
  if (!value.displayName.trim()) errors.displayName = "Укажите название магазина";
  const slug = slugIssue(value.slug);
  if (!value.slug) errors.slug = "Укажите адрес магазина";
  else if (slug) errors.slug = slug;
  if (!value.timeZone) errors.timeZone = "Выберите часовой пояс";
  if (!isEmail(value.ownerEmail)) errors.owner = "Укажите почту владельца";
  else if (lookup?.conflict) errors.owner = OWNER_CONFLICT_TEXT[lookup.conflict];
  if (!errors.owner && !owner && !value.ownerName.trim()) errors.ownerName = "Укажите имя владельца — оно будет в приглашении";
  if (value.contactEmail.trim() && !isEmail(value.contactEmail)) errors.contactEmail = "Почта записана неверно";
  return errors;
}

/** Тело запроса создания. */
export function toCreatePayload(value: ShopCreateValue, lookup: OwnerLookup | null): CreateShopPayload {
  const contactEmail = value.contactEmail.trim();
  const owner = lookup?.user ?? null;
  return {
    slug: value.slug,
    displayName: value.displayName.trim(),
    timeZone: value.timeZone,
    ...(contactEmail ? { contactEmail } : {}),
    owner: owner
      ? { mode: "existing", userId: owner.id }
      : { mode: "new", email: value.ownerEmail.trim(), displayName: value.ownerName.trim() },
  };
}
